import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { Queries, Select } from '@aline/common';
import { FormControlLabel, Typography } from '@mui/material';
import clsx from 'clsx';
import type { PanelVariableType } from 'src/components';
import {
  AlineButton,
  AlineTooltip,
  useOnChangeFocusVariable,
  useSetVariablePanelState,
  useUpdateCheckboxVariableValue,
  useVariableStyles,
  VariableRowHeader,
} from 'src/components';
import { AlineCheckbox } from 'src/components/generic/AlineCheckbox';
import { useGetFieldDisabledReason } from 'src/editor/popovers';
import { domElIdCreators } from 'src/helpers';
import { useGetEditorUserState } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  checkboxContainer: {
    padding: theme.spacing(0.5, 0),
  },
  checkboxList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  checkboxRow: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  skip: {
    marginLeft: 'auto',
  },
  skipButton: {
    color: theme.appColors.primary,
    fontWeight: 'normal !important',
  },
}));

interface OptionsVariableProps extends React.HTMLAttributes<HTMLElement> {
  checkboxVariable: PanelVariableType;
  documentId: UUID;
  isSelected: boolean;
}

export function CheckboxVariable({
  checkboxVariable,
  className,
  documentId,
  isSelected,
}: OptionsVariableProps): JSX.Element {
  const { classes: rowClasses } = useStyles();
  const setVariablePanelState = useSetVariablePanelState();
  const [liEl, setLiEl] = useState<HTMLLIElement | null>(null);
  const [isCheckboxGroupFocused, setIsCheckboxGroupFocused] = useState(false);
  const { disabledReason } = useGetFieldDisabledReason(checkboxVariable);
  const { options, selectedAnswerIds, updateCheckboxVariable } =
    useUpdateCheckboxVariableValue(checkboxVariable);
  const selectedOptionIds = new Set(selectedAnswerIds);
  const { maxRequired, minRequired, skipped } = checkboxVariable;
  const selectedCount = selectedOptionIds.size;
  const isCompleted = Queries.getIsCheckboxComplete({
    maxRequired,
    minRequired,
    selectedCount,
    skipped,
  });
  const isAtMax = maxRequired !== null && selectedCount >= maxRequired;
  const { isInSigningCeremony } = useGetEditorUserState();
  const { classes: variableClasses } = useVariableStyles({
    isCompleted,
    isSelected: isSelected || isCheckboxGroupFocused,
  });

  const { onFocus } = useOnChangeFocusVariable(checkboxVariable, { inputEl: null, liEl });

  return (
    <li
      ref={setLiEl}
      className={clsx(className, variableClasses.variable)}
      data-cy={Select.document.editor.rightDrawer.variable.list.row.container}
      id={domElIdCreators.variableRowEl(checkboxVariable.nodeId)}
      onClick={() => setVariablePanelState({ focusedField: checkboxVariable })}
      onFocus={onFocus}
    >
      <VariableRowHeader
        documentId={documentId}
        isCompleted={isCompleted}
        variable={checkboxVariable}
      />
      <Typography className={variableClasses.question}>{checkboxVariable.question}</Typography>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div
          className={rowClasses.checkboxContainer}
          onBlur={e => {
            if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
            setIsCheckboxGroupFocused(false);
          }}
          onClick={e => e.stopPropagation()}
          onFocusCapture={() => setIsCheckboxGroupFocused(true)}
        >
          <div className={rowClasses.checkboxRow}>
            <div className={rowClasses.checkboxList}>
              {options.map((option, index) => {
                const optionChecked = selectedOptionIds.has(option.id);
                return (
                  <div key={`${option.id}-${index}`} className={rowClasses.checkboxRow}>
                    <FormControlLabel
                      control={
                        <AlineCheckbox
                          checked={optionChecked}
                          disabled={Boolean(disabledReason) || (!optionChecked && isAtMax)}
                          onChange={async e => {
                            const next = new Set(selectedAnswerIds);
                            if (e.target.checked) {
                              if (isAtMax) return;
                              next.add(option.id);
                            } else {
                              next.delete(option.id);
                            }
                            await updateCheckboxVariable([...next]);
                          }}
                        />
                      }
                      label={option.optionText}
                    />
                  </div>
                );
              })}
            </div>
            {isInSigningCeremony && minRequired === 0 && !isCompleted && (
              <AlineButton
                buttonClassName={rowClasses.skipButton}
                className={rowClasses.skip}
                size="small"
                variant="text"
                onClick={e => {
                  e.stopPropagation();
                  void updateCheckboxVariable([], { skipped: true });
                }}
              >
                Skip
              </AlineButton>
            )}
          </div>
        </div>
      </AlineTooltip>
    </li>
  );
}
